import { useCallback, useRef, useEffect } from 'react';
import { supabase } from './supabase';

interface PlaytesterState {
    deckName?: string;
    deckColors: string[];
    deckCoverImage?: string | null;
    format?: string;
    libraryCount: number;
    handCount: number;
    battlefieldCount: number;
    graveyardCount: number;
    exileCount: number;
    commanders?: { name: string; imageUrl?: string }[];
    companion?: { name: string; imageUrl?: string } | null;
    turnNumber: number;
    lifeTotal: number;
}

// Debounce updates to prevent spamming the database
function usePlaytesterSync() {
    const sessionId = useRef<string | null>(null);
    const updateTimeout = useRef<NodeJS.Timeout | null>(null);
    const lastState = useRef<PlaytesterState | null>(null);

    // Start a session when user opens playtester
    const startSession = useCallback(async (initialState: Partial<PlaytesterState>) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        try {
            // Upsert session (one per user)
            const { data, error } = await supabase
                .from('playtester_sessions')
                .upsert({
                    user_id: user.id,
                    deck_name: initialState.deckName || 'Untitled Deck',
                    deck_colors: initialState.deckColors || [],
                    deck_cover_image: initialState.deckCoverImage,
                    format: initialState.format || 'standard',
                    library_count: initialState.libraryCount || 0,
                    hand_count: initialState.handCount || 0,
                    battlefield_count: initialState.battlefieldCount || 0,
                    graveyard_count: initialState.graveyardCount || 0,
                    exile_count: initialState.exileCount || 0,
                    commanders: initialState.commanders || [],
                    companion: initialState.companion || null,
                    turn_number: initialState.turnNumber || 0,
                    life_total: initialState.lifeTotal || 20,
                    is_active: true,
                    started_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                }, { onConflict: 'user_id' })
                .select('id')
                .single();

            if (!error && data) {
                sessionId.current = data.id;
            }
        } catch (err) {
            console.error('Failed to start playtester session:', err);
        }
    }, []);

    // Update session state (debounced)
    const updateSession = useCallback((state: Partial<PlaytesterState>) => {
        // Merge with last known state
        lastState.current = { ...lastState.current, ...state } as PlaytesterState;

        // Clear existing timeout
        if (updateTimeout.current) {
            clearTimeout(updateTimeout.current);
        }

        // Debounce: wait 200ms before pushing to DB
        updateTimeout.current = setTimeout(async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user || !lastState.current) return;

            try {
                await supabase
                    .from('playtester_sessions')
                    .update({
                        deck_name: lastState.current.deckName,
                        deck_colors: lastState.current.deckColors,
                        deck_cover_image: lastState.current.deckCoverImage,
                        format: lastState.current.format,
                        library_count: lastState.current.libraryCount,
                        hand_count: lastState.current.handCount,
                        battlefield_count: lastState.current.battlefieldCount,
                        graveyard_count: lastState.current.graveyardCount,
                        exile_count: lastState.current.exileCount,
                        commanders: lastState.current.commanders,
                        companion: lastState.current.companion,
                        turn_number: lastState.current.turnNumber,
                        life_total: lastState.current.lifeTotal,
                        updated_at: new Date().toISOString(),
                    })
                    .eq('user_id', user.id);
            } catch (err) {
                console.error('Failed to update playtester session:', err);
            }
        }, 200);
    }, []);

    // End session when user closes playtester
    const endSession = useCallback(async () => {
        if (updateTimeout.current) {
            clearTimeout(updateTimeout.current);
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        try {
            await supabase
                .from('playtester_sessions')
                .update({ is_active: false, updated_at: new Date().toISOString() })
                .eq('user_id', user.id);
        } catch (err) {
            console.error('Failed to end playtester session:', err);
        }

        sessionId.current = null;
        lastState.current = null;
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (updateTimeout.current) {
                clearTimeout(updateTimeout.current);
            }
        };
    }, []);

    return { startSession, updateSession, endSession };
}

export { usePlaytesterSync };
export type { PlaytesterState };
